import { useMemo, useState, type ChangeEvent } from 'react';
import { AlertCircle, AlertTriangle, Download, FileUp, Search, ShoppingCart } from 'lucide-react';
import { apiService, type GradeRepairRunDTO, type TebexGrantLineDTO, type TebexGrantPlayerDTO, type TebexGrantPreview, type TebexRunEntryDTO } from '@/services/api.service';
import { formatPlayerName } from '@/utils/playerName';
import { aberrantYear, formatEnd, gradeLabel } from './activeGrades';
import {
  ALLOWED_COLUMNS, CATEGORY_HINTS, CATEGORY_LABELS, CATEGORY_ORDER, cleanExport, computeExpected, DEFAULT_OPTIONS,
  GRADE_RANK, SOLD_GRADES, type CleanedExport, type ImportOptions, type SoldGrade, type TebexCategory,
} from './tebexImport';
import styles from './ActiveGradesSection.module.scss';

const CONFIRM_WORD = 'POSER';
const PAGE = 200;
const RANKED_GRADES = SOLD_GRADES.filter((g) => GRADE_RANK[g] !== undefined);

function download(name: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** État du grade dans LuckPerms, en clair. */
function currentLabel(l: TebexGrantLineDTO): string {
  if (!l.currentGrade) return 'aucun grade vendu';
  const g = gradeLabel(l.currentGrade);
  if (l.currentPermanent) return `${g} à vie`;
  if (l.current === undefined) return g;
  if (l.current > 32_503_680_000) return `${g}, ${aberrantYear(l.current)}`;
  return `${g} jusqu'au ${formatEnd(l.current)}`;
}

function actionLabel(l: TebexGrantLineDTO): string {
  if (l.warning === 'uuid_different') return 'Rien : pseudo connu de LuckPerms sous un autre uuid, à vérifier';
  if (l.action === 'POSER') return `Poser ${gradeLabel(l.grade)} jusqu'au ${formatEnd(l.newEnd!)}`;
  if (l.action === 'REMPLACER' && l.currentPermanent) return `Poser la fin : ${gradeLabel(l.grade)} jusqu'au ${formatEnd(l.newEnd!)}`;
  if (l.action === 'REMPLACER') return `Remplacer par ${gradeLabel(l.grade)} jusqu'au ${formatEnd(l.newEnd!)}`;
  if (!l.needed && l.category === 'COUVERT') return 'Rien : un grade plus haut acheté dure plus longtemps';
  return 'Rien';
}

function statusLabel(s?: string): { text: string; ok: boolean } {
  switch (s) {
    case 'pose': return { text: 'Posé', ok: true };
    case 'remplace': return { text: 'Remplacé', ok: true };
    case 'echec_pose': return { text: 'Échec : rien n\'a changé pour ce grade', ok: false };
    case 'echec_retrait': return { text: 'Échec : rien n\'a changé pour ce grade', ok: false };
    case 'prevu': return { text: 'Non traité (pose interrompue)', ok: false };
    case 'retire_sans_remise': return { text: 'Échec : ancien node retiré mais nouveau pas posé, à reposer à la main (ancien node au journal)', ok: false };
    default: return { text: s ?? '', ok: false };
  }
}

/**
 * Comparaison de LuckPerms avec l'export des paiements Tebex, puis pose des grades dus (admins du panel).
 *
 * <p>L'export est lu ICI, dans le navigateur, et réduit aux colonnes de ALLOWED_COLUMNS dès la lecture :
 * le reste (nom, adresse, pays, e-mail, IP, montants) n'est ni gardé, ni affiché, ni envoyé. Les grades
 * dus sont calculés ici aussi (règles de la boutique, options ci-dessous) ; le satellite ne reçoit que
 * XUID, pseudo Tebex, grades dus et achats qui les expliquent.</p>
 */
export function TebexImportPanel({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<{ name: string; cleaned: CleanedExport } | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [options, setOptions] = useState<ImportOptions>(DEFAULT_OPTIONS);
  const [excluded, setExcluded] = useState(DEFAULT_OPTIONS.excludeManualFrom.join(', '));
  // Cas principal en production (grades arrivés à vie par « experity ») : coché par défaut.
  const [replaceLifetime, setReplaceLifetime] = useState(true);
  const [preview, setPreview] = useState<TebexGrantPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TebexCategory | 'all' | 'actions'>('actions');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [showExtras, setShowExtras] = useState(false);
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<GradeRepairRunDTO | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const nowSec = Math.floor(Date.now() / 1000);
  const expected = useMemo(() => (file ? computeExpected(file.cleaned.payments, options, nowSec) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [file, options]);
  const payload: TebexGrantPlayerDTO[] = useMemo(() => (expected?.players ?? []).map((p) => ({
    xuid: p.xuid,
    username: p.username,
    grants: p.grants.map((g) => ({ grade: g.grade, end: g.end, needed: g.needed, purchases: g.purchases })),
    explained: p.explained,
  })), [expected]);

  const invalidate = () => { setPreview(null); setResult(null); setChecked(false); setTyped(''); setError(null); };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    invalidate();
    setReadError(null);
    if (!f) return;
    try {
      // Le texte brut ne sort pas de cette fonction : seules les colonnes gardées sont conservées.
      const cleaned = cleanExport(await f.text());
      if (cleaned.missing.length) {
        setFile(null);
        setReadError(`Ce fichier n'est pas un export des paiements Tebex : colonnes absentes (${cleaned.missing.join(', ')}).`);
        return;
      }
      setFile({ name: f.name, cleaned });
    } catch {
      setReadError('Fichier illisible.');
    }
  };

  const setOption = <K extends keyof ImportOptions>(key: K, value: ImportOptions[K]) => {
    setOptions((o) => ({ ...o, [key]: value }));
    invalidate();
  };

  const compare = async () => {
    if (!payload.length) return;
    setLoading(true);
    invalidate();
    const res = await apiService.previewTebexGrants(payload, replaceLifetime);
    setLoading(false);
    if (res.status === 404) setError("Le satellite de ce projet ne sert pas encore cette comparaison : il faut publier sa nouvelle version.");
    else if (res.error) setError(res.error);
    else if (res.data) { setPreview(res.data); setFilter(res.data.toPose + res.data.toReplace > 0 ? 'actions' : 'all'); setShown(PAGE); }
  };

  const apply = async () => {
    if (!preview || !canApply) return;
    setApplying(true);
    setError(null);
    const res = await apiService.applyTebexGrants(payload, replaceLifetime, preview.fingerprint);
    setApplying(false);
    if (res.error) {
      setError(res.error);
      if (res.status === 409) void compare();
      return;
    }
    if (!res.data) return;
    // La pose tourne en tâche de fond dans le satellite : on suit son journal jusqu'à la fin.
    const runId = res.data.runId;
    setProgress({ done: 0, total: res.data.total });
    for (let i = 0; i < 1800; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const runs = await apiService.getGradeRepairRuns();
      const run = runs.data?.runs.find((r) => r._id === runId);
      if (!run) continue;
      setProgress({ done: run.done ?? 0, total: run.total ?? res.data.total });
      if (run.status !== 'en_cours') {
        setResult(run);
        setProgress(null);
        onDone();
        return;
      }
    }
    setError(`La pose ${runId} n'a pas fini après une heure : son état est au journal.`);
  };

  const actionable = preview ? preview.toPose + preview.toReplace : 0;
  // Comptes dans les termes du relevé de Claude Desktop : grades à vie datés, autres dates corrigées.
  const toDate = preview ? preview.lines.filter((l) => l.category === 'A_VIE' && l.action === 'REMPLACER').length : 0;
  const otherDates = preview ? preview.toReplace - toDate : 0;
  const canApply = !!preview && preview.journalAvailable && !preview.running && actionable > 0
    && preview.failures.length === 0 && checked && typed.trim() === CONFIRM_WORD && !applying && !progress;

  const lines = useMemo(() => {
    if (!preview) return [];
    const q = query.trim().toLowerCase();
    return preview.lines.filter((l) => {
      if (filter === 'actions' && l.action === 'AUCUNE') return false;
      if (filter !== 'all' && filter !== 'actions' && l.category !== filter) return false;
      if (!q) return true;
      return [l.username, l.lpName, l.displayName, l.xuid, l.uuid].some((v) => v && v.toLowerCase().includes(q));
    }).sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
      || a.username.localeCompare(b.username, 'fr'));
  }, [preview, filter, query]);

  const stats = expected?.stats;

  return (
    <div className={styles.repair}>
      <div className={styles.repairHead}>
        <h3><ShoppingCart size={18} /> Comparer LuckPerms à l'export Tebex</h3>
        <button className={styles.buttonGhost} onClick={onClose}>Fermer</button>
      </div>

      <p className={styles.repairIntro}>
        L'export des paiements est lu <strong>dans ton navigateur</strong>. Seules ces colonnes sont gardées :
        {' '}{ALLOWED_COLUMNS.join(', ')}. Les autres (nom, adresse, pays, e-mail, IP, montants) sont jetées à la
        lecture : elles ne sont ni affichées, ni envoyées. Le satellite ne reçoit que le XUID, le pseudo Tebex et les
        grades dus.
      </p>

      <label className={styles.fileInput}>
        <FileUp size={16} />
        <span>{file ? `Export : ${file.name}` : 'Choisir l\'export des paiements Tebex (.csv)'}</span>
        <input type="file" accept=".csv,text/csv" onChange={onFile} />
      </label>
      {readError && <div className={styles.errorBox}><AlertCircle size={16} /> {readError}</div>}
      {file && (
        <p className={styles.hint}>
          {file.cleaned.payments.length} paiements lus. Colonnes écartées sans être gardées : {file.cleaned.dropped.join(', ') || 'aucune'}.
        </p>
      )}

      {file && stats && (
        <>
          <div className={styles.optionsBox}>
            <label className={styles.check}>
              <input type="checkbox" checked={options.ultimateV1} onChange={(e) => setOption('ultimateV1', e.target.checked)} />
              Pack Ultimate V1 donne Divinité 6 mois
              <span className={styles.muted}>(question ouverte, non par défaut{!options.ultimateV1 && stats.ultimateIgnored ? ` : ${stats.ultimateIgnored} achat${stats.ultimateIgnored > 1 ? 's' : ''} ignoré${stats.ultimateIgnored > 1 ? 's' : ''}` : ''})</span>
            </label>
            <label className={styles.check}>
              <input type="checkbox" checked={options.cumulate} onChange={(e) => setOption('cumulate', e.target.checked)} />
              Deux achats du même grade s'additionnent (le second part de la fin du premier)
            </label>
            <label className={styles.typed}>
              « Grade Paladium » donne
              <select className={styles.select} value={options.paladiumGrade} onChange={(e) => setOption('paladiumGrade', e.target.value as SoldGrade)}>
                {RANKED_GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
              </select>
              et « Grade Titan »
              <select className={styles.select} value={options.titanGrade} onChange={(e) => setOption('titanGrade', e.target.value as SoldGrade)}>
                {RANKED_GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
              </select>
            </label>
            <label className={styles.typed}>
              Commandes manuelles de test écartées (pseudos) :
              <input value={excluded} onChange={(e) => { setExcluded(e.target.value); setOption('excludeManualFrom', e.target.value.split(',')); }} />
            </label>
            <label className={styles.check}>
              <input type="checkbox" checked={replaceLifetime} onChange={(e) => { setReplaceLifetime(e.target.checked); invalidate(); }} />
              Poser la date de fin de l'achat sur les grades arrivés à vie par erreur (« experity »)
              <span className={styles.muted}>(oui par défaut ; un grade à vie sans achat Tebex n'est jamais touché)</span>
            </label>
          </div>

          <p className={styles.repairIntro}>
            <strong>{stats.players} joueurs</strong> ont encore droit à un grade :{' '}
            {RANKED_GRADES.filter((g) => stats.byTopGrade[g]).map((g) => `${gradeLabel(g)} ${stats.byTopGrade[g]}`).join(', ') || 'aucun grade'}
            {' '}; Premium seul {stats.premiumOnly}, Premium en plus d'un grade {stats.premiumPlus} ; LuckyPass {stats.luckypass}.
            {' '}<span className={styles.muted}>
              {stats.counted} paiements comptés, {stats.declined} refusés ignorés
              {stats.excludedManual ? `, ${stats.excludedManual} commande manuelle de test écartée` : ''}.
            </span>
          </p>

          <div className={styles.confirm}>
            <button className={styles.button} onClick={compare} disabled={loading || !payload.length}>
              {loading ? 'Lecture de LuckPerms...' : 'Comparer avec LuckPerms'}
            </button>
            <span className={styles.hint}>Rien n'est écrit : c'est un aperçu.</span>
          </div>
        </>
      )}

      {error && <div className={styles.errorBox}><AlertCircle size={16} /> {error}</div>}

      {preview && !result && (
        <>
          <div className={styles.repairCounts}>
            <button className={`${styles.pillFilter} ${filter === 'actions' ? styles.pillActive : ''}`} onClick={() => { setFilter('actions'); setShown(PAGE); }}>
              À traiter : {actionable}
            </button>
            {CATEGORY_ORDER.map((c) => (
              <button key={c} className={`${styles.pillFilter} ${styles['cat' + c]} ${filter === c ? styles.pillActive : ''}`}
                onClick={() => { setFilter(c); setShown(PAGE); }} title={CATEGORY_HINTS[c]}>
                {CATEGORY_LABELS[c]} : {preview.counts[c] ?? 0}
              </button>
            ))}
            <button className={`${styles.pillFilter} ${filter === 'all' ? styles.pillActive : ''}`} onClick={() => { setFilter('all'); setShown(PAGE); }}>
              Tout : {preview.lines.length}
            </button>
          </div>

          <div className={styles.repairCounts}>
            <span className={styles.pillFix}>À dater (grades à vie par erreur) : {toDate}</span>
            <span className={styles.pillFix}>À poser (achats en cours sans grade) : {preview.toPose}</span>
            {otherDates > 0 && <span className={styles.pillFix}>Autres dates corrigées : {otherDates}</span>}
            <span className={styles.pillIgnore}>À retirer : 0 (la pose ne retire jamais un grade)</span>
            <span className={styles.pillIgnore}>Sans achat dans l'export, laissés tels quels : {preview.extraCount}</span>
          </div>
          {preview.expiredLifetime.length > 0 && (
            <div className={styles.errorBox}>
              <AlertCircle size={16} />
              {preview.expiredLifetime.length} grade{preview.expiredLifetime.length > 1 ? 's' : ''} à vie dont tous les achats sont finis
              ({preview.expiredLifetime.slice(0, 8).map((x) => `${x.name ?? x.uuid.slice(0, 8)} : ${gradeLabel(x.grade)}`).join(', ')}
              {preview.expiredLifetime.length > 8 ? '…' : ''}) : le joueur devrait l'avoir perdu. Ils ne sont pas retirés ici.
            </div>
          )}

          <div className={styles.notice}>
            <AlertTriangle size={16} />
            <span>
              {preview.players} joueurs comparés. {preview.toPose} grade{preview.toPose > 1 ? 's' : ''} à poser,
              {' '}{preview.toReplace} fin{preview.toReplace > 1 ? 's' : ''} à remplacer par une date plus lointaine. Aucun grade n'est
              retiré, aucune fin n'est rapprochée, aucun grade du staff n'est touché.
              {preview.renamed > 0 && ` ${preview.renamed} joueur${preview.renamed > 1 ? 's ont' : ' a'} changé de pseudo depuis l'achat (signalé, le XUID ne change pas).`}
              {preview.unknown > 0 && ` ${preview.unknown} joueur${preview.unknown > 1 ? 's' : ''} jamais vu${preview.unknown > 1 ? 's' : ''} par LuckPerms : le grade l'attendra à sa première connexion.`}
              {preview.uuidMismatch > 0 && ` ${preview.uuidMismatch} pseudo${preview.uuidMismatch > 1 ? 's' : ''} connu${preview.uuidMismatch > 1 ? 's' : ''} de LuckPerms sous un autre uuid : rien n'est posé pour eux, à vérifier.`}
            </span>
          </div>
          {!preview.journalAvailable && <div className={styles.errorBox}><AlertCircle size={16} /> Pas de base « satellite » : sans journal, la pose est refusée.</div>}
          {preview.failures.length > 0 && <div className={styles.errorBox}><AlertCircle size={16} /> LuckPerms n'a pas répondu pour : {preview.failures.join(', ')}. Pose impossible.</div>}

          <label className={styles.search}>
            <Search size={16} />
            <input value={query} onChange={(e) => { setQuery(e.target.value); setShown(PAGE); }} placeholder="Rechercher un pseudo, un XUID ou un uuid" />
          </label>

          {lines.length === 0 ? (
            <p className={styles.empty}>Aucune ligne pour ce filtre.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>Joueur</th><th>Grade dû</th><th>Dans LuckPerms</th><th>Catégorie</th><th>Ce qui sera fait</th></tr>
                </thead>
                <tbody>
                  {lines.slice(0, shown).map((l) => (
                    <tr key={`${l.uuid}-${l.grade}`}>
                      <td>
                        <div className={styles.playerText}>
                          <span className={styles.playerName}>{formatPlayerName(l.username, l.displayName, l.xuid)}</span>
                          {l.renamed && <span className={styles.chipWarn}>pseudo changé : {l.lpName} dans LuckPerms</span>}
                          {l.warning === 'inconnu' && <span className={styles.chipInfo}>jamais vu par LuckPerms</span>}
                          {l.warning === 'uuid_different' && <span className={styles.chipDanger}>autre uuid sous ce pseudo</span>}
                        </div>
                      </td>
                      <td>
                        <span className={styles.gradeCell}><span className={styles.gradeTag}>{gradeLabel(l.grade)}</span></span>
                        <span className={styles.realDate}>jusqu'au <strong>{formatEnd(l.expectedEnd)}</strong>{l.purchases > 1 ? `, ${l.purchases} achats` : ''}</span>
                      </td>
                      <td className={styles.muted}>{currentLabel(l)}</td>
                      <td><span className={styles['catText' + l.category]} title={CATEGORY_HINTS[l.category]}>{CATEGORY_LABELS[l.category]}</span></td>
                      <td className={l.action === 'AUCUNE' ? styles.muted : styles.actionFix}>{actionLabel(l)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {lines.length > shown && <button className={styles.more} onClick={() => setShown(shown + PAGE)}>Afficher plus ({lines.length - shown} de plus)</button>}

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <h3><AlertTriangle size={18} /> Grades en trop : {preview.extraCount} sur {preview.extraPlayers} joueur{preview.extraPlayers > 1 ? 's' : ''}</h3>
              <button className={styles.buttonGhost} onClick={() => setShowExtras(!showExtras)}>{showExtras ? 'Masquer' : 'Afficher'}</button>
            </div>
            <p className={styles.cardNote}>
              Grades vendus présents dans LuckPerms qu'aucun achat de l'export n'explique : donnés par le staff, achetés avant
              l'export, ou en trop. Ils sont seulement signalés : la pose ne les retire jamais.
            </p>
            {showExtras && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead><tr><th>Joueur</th><th>Grade</th><th>Fin</th><th>Dans l'export</th></tr></thead>
                  <tbody>
                    {preview.extras.map((x) => (
                      <tr key={`${x.uuid}-${x.grade}`}>
                        <td>{formatPlayerName(x.name, x.displayName, x.uuid.slice(0, 13) + '…')}</td>
                        <td><span className={styles.gradeTag}>{gradeLabel(x.grade)}</span></td>
                        <td className={styles.muted}>{x.permanent ? 'à vie' : x.expiry ? (x.expiry > 32_503_680_000 ? aberrantYear(x.expiry) : formatEnd(x.expiry)) : ''}</td>
                        <td className={styles.muted}>{x.inExport ? 'oui (autre grade acheté)' : 'non'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {actionable > 0 && (
            <div className={styles.confirm}>
              <button className={styles.buttonGhost} onClick={() => download(`apercu-grades-tebex-${preview.fingerprint}.json`, preview)}>
                <Download size={16} /> Télécharger l'aperçu
              </button>
              <label className={styles.check}>
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                J'ai relu la liste : {preview.toPose} grade{preview.toPose > 1 ? 's' : ''} posé{preview.toPose > 1 ? 's' : ''}, {preview.toReplace} fin{preview.toReplace > 1 ? 's' : ''} remplacée{preview.toReplace > 1 ? 's' : ''}.
              </label>
              <label className={styles.typed}>
                Tape <code>{CONFIRM_WORD}</code> pour confirmer :
                <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={CONFIRM_WORD} autoComplete="off" />
              </label>
              <button className={styles.buttonDanger} disabled={!canApply} onClick={apply}>
                {applying ? 'Lancement...' : progress ? `Pose en cours : ${progress.done} / ${progress.total}` : `Poser ${actionable} grade${actionable > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
          <p className={styles.hint}>
            Chaque changement est écrit au journal du satellite avec l'ancien node, pour pouvoir revenir en arrière. Un joueur
            connecté reçoit son grade à sa reconnexion.
          </p>
        </>
      )}

      {result && (() => {
        const entries = (result.entries as unknown as TebexRunEntryDTO[]);
        const failed = result.failed ?? 0;
        return (
          <>
            <div className={failed > 0 || result.status === 'interrompu' ? styles.errorBox : styles.successBox}>
              {failed > 0 ? <AlertCircle size={16} /> : <ShoppingCart size={16} />}
              {result.posed ?? 0} posé{(result.posed ?? 0) > 1 ? 's' : ''}, {result.fixed ?? 0} daté{(result.fixed ?? 0) > 1 ? 's' : ''} ou remplacé{(result.fixed ?? 0) > 1 ? 's' : ''}
              {failed > 0 ? `, ${failed} en échec (détail ci-dessous)` : ''}{result.status === 'interrompu' ? ', pose interrompue (voir le journal du satellite)' : ''}. Journal : {result._id}
            </div>
            <button className={styles.buttonGhost} onClick={() => download(`journal-grades-tebex-${result._id}.json`, result)}>
              <Download size={16} /> Télécharger le journal
            </button>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>Joueur</th><th>Grade</th><th>Avant</th><th>Après</th><th>Résultat</th></tr></thead>
                <tbody>
                  {entries.map((e) => {
                    const st = e.oldPermanent && e.status === 'remplace' ? { text: 'Daté', ok: true } : statusLabel(e.status);
                    const before = e.oldPermanent ? 'à vie' : e.oldExpiry ? (e.oldExpiry > 32_503_680_000 ? aberrantYear(e.oldExpiry) : `jusqu'au ${formatEnd(e.oldExpiry)}`) : 'aucun';
                    return (
                      <tr key={`${e.uuid}-${e.grade}`}>
                        <td>{e.name || e.xuid}</td>
                        <td><span className={styles.gradeTag}>{gradeLabel(e.grade)}</span></td>
                        <td className={styles.muted}>{before}</td>
                        <td>{e.newExpiry ? `jusqu'au ${formatEnd(e.newExpiry)}` : ''}</td>
                        <td className={st.ok ? styles.actionFix : styles.actionRemove}>{st.text}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}
    </div>
  );
}
